import React, { useEffect, useMemo, useState } from 'react';
import { useWidth } from '../../../hooks/useWidth';
import CircularProgress from '../../common/CircularProgress';
import type { AppointmentType } from '../../../contexts/PsychologistContext';
import { localTzToUTC, isoToZoneParts } from './timeZoneHelpers';
import { Clock, X, User, CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchPatientsOnce, type Patient } from './data';
import { isImpersonated } from '../../../hooks/api/auth/tokenManager';
import { needsCoordination } from './coordinationWindow';
import { formatDayDdMmYyyy } from './utils';
import warningIcon from '../../../assets/warningTriangle.svg';
import { timezoneToCountryCode, countryCodeToName } from '../../../utils/timezoneToCountry';
import CountryFlag from '../../common/CountryFlag';
import { BA_TIMEZONE } from '../../../utils/timezone';

type Props = {
  open: boolean;
  onClose: () => void;
  date: string;
  startTime: string;
  endTime?: string;
  selectedPatientEmail: string;
  selectedPatientName?: string;
  onConfirm: () => Promise<void> | void;
  loading?: boolean;
  appointmentType?: AppointmentType;
  selectedTimeZone?: string;
  patientTimezone?: string;
  dstDates?: { start: string; end: string; diffHours: number } | null;
};

const BookSessionModal: React.FC<Props> = ({ open, onClose, date, startTime, endTime, selectedPatientEmail, selectedPatientName, onConfirm, loading, appointmentType, selectedTimeZone = BA_TIMEZONE, patientTimezone, dstDates }) => {
  const { size } = useWidth();
  const therapistCountry = countryCodeToName(timezoneToCountryCode(selectedTimeZone)) || 'Argentina';
  const isMobile = size === 's';
  const [isUrgentPatient, setIsUrgentPatient] = useState(false);
  // Validar que haya al menos 6 horas de anticipación
  const hasMinimumAdvanceTime = useMemo(() => {
    try {
      // Pacientes urgentes no tienen restricción de anticipación mínima
      if (isUrgentPatient) return true;

      const now = new Date();
      const nowMs = now.getTime();
      const SIX_HOURS_MS = 6 * 60 * 60 * 1000; // 6 horas en milisegundos

      // Convertir la fecha/hora del slot (en la zona horaria del psicólogo) a UTC
      const slotDateTimeUTC = localTzToUTC(date, startTime, selectedTimeZone);
      const slotMs = slotDateTimeUTC.getTime();

      // Calcular la diferencia en milisegundos
      const timeDiffMs = slotMs - nowMs;

      if (isImpersonated()) {
        return true;
      }
      return timeDiffMs >= SIX_HOURS_MS;
    } catch (error) {
      console.error('Error validando tiempo de anticipación:', error);
      return false;
    }
  }, [date, startTime, selectedTimeZone, isUrgentPatient]);
  
  // Revalidación de "Requiere coordinación" al momento de confirmar — no depende de
  // que la celda de origen haya pasado por el chequeo de hover/click en CalendarCell
  // (esa es sólo una de las formas de llegar a este modal). Ventana móvil de 24hs
  // real, no "cae en el día calendario siguiente": ver coordinationWindow.ts.
  const requiresCoordination = useMemo(() => {
    try {
      if (isImpersonated()) return false;
      const slotDateTimeUTC = localTzToUTC(date, startTime, selectedTimeZone);
      return needsCoordination(slotDateTimeUTC);
    } catch {
      return false;
    }
  }, [date, startTime, selectedTimeZone]);

  const canConfirm = selectedPatientEmail && !loading && hasMinimumAdvanceTime;
  const formatDdMmYyyy = (s: string): string => {
    return formatDayDdMmYyyy(s);
  };
  
  const { psychologistId } = useAuth();
  const [patientProcessType, setPatientProcessType] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!open || !psychologistId) return;
        const patients: Patient[] = await fetchPatientsOnce(psychologistId);
        const byEmail = patients.find(p => (p.email || '').toLowerCase() === (selectedPatientEmail || '').toLowerCase());
        if (!cancelled) {
          setPatientProcessType(byEmail?.processType || undefined);
          setIsUrgentPatient(byEmail?.isUrgent ?? false);
        }
      } catch {
        if (!cancelled) {
          setPatientProcessType(undefined);
          setIsUrgentPatient(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, psychologistId, selectedPatientEmail]);

  const typeLabel = patientProcessType || appointmentType?.name || 'Individual';
  const durationDisplay = appointmentType?.duration || 60;

  const hasDstChange = useMemo(() => {
    if (!dstDates || !date || !startTime) return false;
    const now = new Date();
    const slotUtc = localTzToUTC(date, startTime, selectedTimeZone);
    const dstStart = new Date(dstDates.start);
    const dstEnd = new Date(dstDates.end);
    const nowInDst = now >= dstStart && now <= dstEnd;
    const slotInDst = slotUtc >= dstStart && slotUtc <= dstEnd;
    return nowInDst !== slotInDst;
  }, [dstDates, date, startTime, selectedTimeZone]);

  // Compute patient time in their timezone.
  // Always shown when patientTimezone is set — even if it matches the psychologist's timezone,
  // so the card unambiguously shows both perspectives for any patient.
  const patientTimeInfo = useMemo(() => {
    if (!patientTimezone) return null;
    try {
      const startUTC = localTzToUTC(date, startTime, selectedTimeZone);
      const patientStart = isoToZoneParts(startUTC.toISOString(), patientTimezone);
      let patientEnd: { date: string; time: string } | undefined;
      if (endTime) {
        const endUTC = localTzToUTC(date, endTime, selectedTimeZone);
        patientEnd = isoToZoneParts(endUTC.toISOString(), patientTimezone);
      }
      // Timezone label: city name (e.g., "Madrid") with country flag
      const iso2 = timezoneToCountryCode(patientTimezone);
      const tzLabel = patientTimezone.split('/').pop()?.replace(/_/g, ' ') ?? patientTimezone;
      // Day offset like airports: +1 / -1 if patient date differs from psychologist date
      const psyDate = new Date(date + 'T00:00:00');
      const patDate = new Date(patientStart.date + 'T00:00:00');
      const dayDiff = Math.round((patDate.getTime() - psyDate.getTime()) / 86400000);
      const dayTag = dayDiff > 0 ? `+${dayDiff}` : dayDiff < 0 ? `${dayDiff}` : '';
      return {
        time: patientEnd ? `${patientStart.time} a ${patientEnd.time}` : patientStart.time,
        label: tzLabel,
        iso2,
        dayTag,
      };
    } catch {
      return null;
    }
  }, [date, startTime, endTime, selectedTimeZone, patientTimezone]);

  // Hora en Argentina, como referencia operativa. Se omite si la psicóloga ya está en
  // Argentina: sería exactamente la misma hora repetida dos veces.
  const argentinaTimeInfo = useMemo(() => {
    if (timezoneToCountryCode(selectedTimeZone) === 'AR') return null;
    try {
      const startUTC = localTzToUTC(date, startTime, selectedTimeZone);
      const start = isoToZoneParts(startUTC.toISOString(), BA_TIMEZONE);
      if (!endTime) return { time: start.time };
      const endUTC = localTzToUTC(date, endTime, selectedTimeZone);
      const end = isoToZoneParts(endUTC.toISOString(), BA_TIMEZONE);
      return { time: `${start.time} a ${end.time}` };
    } catch {
      return null;
    }
  }, [date, startTime, endTime, selectedTimeZone]);

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        direction: 'ltr',
        zIndex: 1299
      }}
      onClick={() => { if (!loading) onClose(); }}
    >
      <div
        id="book-session-modal-content"
        className="modal-content"
        style={{
          background: '#fff',
          borderRadius: isMobile ? '20px 20px 0 0' : '16px',
          width: isMobile ? '100%' : '32rem',
          maxWidth: isMobile ? '100%' : '90vw',
          maxHeight: isMobile ? 'calc(100vh - 64px)' : undefined,
          overflowY: isMobile ? 'auto' : undefined,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {isMobile && (
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>
        )}
        {loading ? (
          <div style={{ padding: '3rem', display: 'grid', placeItems: 'center' }}>
            <CircularProgress sizeRem={2.5} label="Agendando" />
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-semibold text-foreground m-0">Confirmar agendamiento</h2>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Card destacado con fecha/hora */}
            <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 mb-5">
              <p className="text-xs text-muted-foreground mb-1">Confirmar agendamiento</p>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-2xl font-semibold m-0 text-[#6D57BC]">{formatDdMmYyyy(date)}</p>
                  {/* 1. Tu horario */}
                  <div data-testid="book-session-therapist-tz" className="flex items-center gap-1.5 mt-1">
                    <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    <span className="text-muted-foreground text-sm whitespace-nowrap flex items-center gap-1">
                      <CountryFlag timezone={selectedTimeZone} fallbackIso2="AR" size="0.875rem" />
                      {endTime ? `${startTime} a ${endTime}` : startTime}
                      <span className="text-xs text-muted-foreground/70">({therapistCountry})</span>
                    </span>
                    <span className="text-[0.7rem] text-muted-foreground/60 font-normal ml-1 whitespace-nowrap">Tu horario</span>
                  </div>

                  {/* 2. Horario Paciente */}
                  {patientTimeInfo ? (
                    <div
                      data-testid="book-session-patient-tz"
                      className="flex items-center gap-1.5 mt-1"
                      title={hasDstChange ? `El horario del paciente tiene +${dstDates?.diffHours || 1}h por cambio de horario en ${patientTimeInfo.label}` : undefined}
                    >
                      <User className={`w-3.5 h-3.5 flex-shrink-0 ${hasDstChange ? 'text-orange-500' : 'text-muted-foreground'}`} />
                      <span className={`text-sm whitespace-nowrap flex items-center gap-1 ${hasDstChange ? 'text-orange-500 font-semibold' : 'text-muted-foreground'}`}>
                        {patientTimeInfo.iso2 && <CountryFlag iso2={patientTimeInfo.iso2} size="0.875rem" />}
                        {patientTimeInfo.time}
                        {patientTimeInfo.dayTag && (
                          <sup className="text-[10px] font-semibold text-orange-500 ml-0.5">{patientTimeInfo.dayTag}</sup>
                        )}
                        <span className={`text-xs ${hasDstChange ? 'text-orange-500/70' : 'text-muted-foreground/70'}`}>({patientTimeInfo.label})</span>
                      </span>
                      <span className="text-[0.7rem] text-muted-foreground/60 font-normal whitespace-nowrap">Horario Paciente</span>
                    </div>
                  ) : (
                    <div data-testid="book-session-patient-tz-missing" className="flex items-center gap-1.5 mt-1">
                      <User className="w-3.5 h-3.5 text-muted-foreground/60 flex-shrink-0" />
                      <span className="text-xs text-muted-foreground/60 whitespace-nowrap">Paciente sin huso horario registrado</span>
                    </div>
                  )}

                  {/* 3. Hora en Argentina, como referencia */}
                  {argentinaTimeInfo && (
                    <div data-testid="book-session-argentina-tz" className="flex items-center gap-1.5 mt-0.5 opacity-50">
                      <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center gap-1 ml-5">
                        <CountryFlag iso2="AR" size="0.75rem" />
                        {argentinaTimeInfo.time}
                      </span>
                      <span className="text-[0.7rem] text-muted-foreground whitespace-nowrap">Horario Argentina</span>
                    </div>
                  )}
                </div>
                <div className={`flex items-center gap-1 bg-card border border-border rounded-full flex-shrink-0 whitespace-nowrap ${isMobile ? 'px-2 py-1' : 'px-3 py-1.5'}`}>
                  <Clock className={`text-muted-foreground flex-shrink-0 ${isMobile ? 'w-3 h-3' : 'w-4 h-4'}`} />
                  <span className={`text-muted-foreground ${isMobile ? 'text-xs' : 'text-sm'}`}>{durationDisplay} minutos</span>
                </div>
              </div>
            </div>

            {/* Requiere coordinación: agendamiento dentro de las próximas 24hs reales */}
            {requiresCoordination && (
              <div
                data-testid="book-session-requires-coordination"
                className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-xs text-amber-700 flex gap-3 items-start"
              >
                <span className="text-lg leading-none flex-shrink-0" aria-hidden="true">&#128337;</span>
                <div>
                  <p className="font-semibold mb-1">Requiere coordinación</p>
                  <p>
                    No es posible agendar con menos de 24hs de anticipación. Podés contactar a CX para coordinarlo.
                  </p>
                  <a
                    href={`https://api.whatsapp.com/send/?phone=5491124084954&text=${encodeURIComponent(`Hola, necesito coordinar una sesión para el ${date.split('-').reverse().join('/')} a las ${startTime}hs Arg${selectedPatientName ? ` con el/la paciente ${selectedPatientName}` : ''}.`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-flex items-center gap-1.5 font-semibold text-[#128C7E] hover:underline"
                  >
                    Contactar a CX
                  </a>
                </div>
              </div>
            )}

            {/* DST warning */}
            {hasDstChange && patientTimeInfo && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-5 text-xs text-amber-700 flex gap-3 items-center">
                <img src={warningIcon} alt="" width={18} height={18} className="flex-shrink-0" />
                <div>
                <p className="font-semibold mb-1">Cambio de horario</p>
                <p>
                  Tené en cuenta que tu paciente se encuentra en {patientTimeInfo.label}, el cual tendrá cambio de horario. No tenés que realizar ningún cambio de tu lado, solo validar el horario con tu paciente.
                </p>
                </div>
              </div>
            )}

            {/* Detalles */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-5 h-5" />
                  <span>Paciente</span>
                </div>
                <span className="font-medium text-foreground">{selectedPatientName || selectedPatientEmail}</span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CalendarDays className="w-5 h-5" />
                  <span>Tipo de cita</span>
                </div>
                <span className="font-medium text-foreground">{typeLabel}</span>
              </div>
            </div>

            {/* Footer con botones */}
            <div className="flex gap-3">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancelar
              </Button>
              <Button
                onClick={onConfirm}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={!canConfirm}
              >
                Confirmar cita
              </Button>
            </div>

            {/* Mensaje de restricción si no hay 6 horas de anticipación */}
            {!hasMinimumAdvanceTime && (
              <div className="mt-3 text-xs text-red-600">
                No se puede agendar con menos de 6 horas de anticipación.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookSessionModal;


